import { useEffect, useMemo } from 'react'
import { useTranslation } from 'next-i18next'

import GradeLevels from '~/components/PanelSettingsForm/GradeLevels'
import PersonalInfo from '~/components/PanelSettingsForm/PersonalInfo'
import PreferredDays from '~/components/PanelSettingsForm/PreferredDays'
import SchoolCountry from '~/components/PanelSettingsForm/SchoolCountry'
import Traits from '~/components/PanelSettingsForm/Traits'
import useSettingsFormContext from '~/hooks/useSettingsFormContext'
import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'
import { LevelOfEducation, Subject } from '~/types/booking'
import { UserRole } from '~/types/user-info-types'
import { UserSettingsErrors } from '~/contexts/SettingsFormContext'

export default function CompletePreferencesForm(): React.ReactElement {
  const { t } = useTranslation(['errors', 'studentPanel'])
  const { fields, setErrors, onSubmit } = useSettingsFormContext()

  const formErrors = useMemo(() => {
    const requiredFields = {
      countryCallingCode: fields.countryCallingCode,
      phone: fields.phone,
      fullName: fields.fullName,
      schoolCountry: fields.schoolCountry,
      timeslots: fields.timeslots,
      traits: fields.traits,
    }

    return Object.entries(requiredFields).reduce((currErrors, [key, value]) => {
      if (!value || (typeof value === 'string' && value.trim().length === 0)) {
        currErrors[key] = t('errors:fieldRequired')
      }

      if (Array.isArray(value) && value.length === 0) {
        currErrors[key] = t('errors:chooseAtLeastOneValue')
      }

      return currErrors
    }, {} as UserSettingsErrors)
  }, [fields, t])

  useEffect(() => {
    setErrors(formErrors)
  }, [formErrors, setErrors])

  const hasError = Object.keys(formErrors).length > 0

  const handleOnSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    if (hasError) return
    onSubmit(fields)
  }

  const shouldDisplayGradeLevels =
    Array.isArray(fields.subjects.levelsOfEducation) &&
    fields.subjects.levelsOfEducation.includes(LevelOfEducation.GRADE_11_TO_13) &&
    fields.subjects.subjects.some((elem) => elem.name === Subject.MATH) &&
    fields.schoolCountry === 'Norway'

  return (
    <form className="lg:max-w-md space-y-4" onSubmit={handleOnSubmit}>
      <PersonalInfo role={UserRole.USER} />
      <SchoolCountry />
      <PreferredDays role={UserRole.USER} />
      <Traits role={UserRole.USER} />
      {shouldDisplayGradeLevels && <GradeLevels />}

      <div className="flex flex-col lg:flex-row-reverse space-y-4 lg:space-y-0 lg:space-x-4 lg:space-x-reverse">
        <ButtonPrimary disabled={hasError} isFullWidth size="small" type="submit">
          {t('studentPanel:completePreferencesButton')}
        </ButtonPrimary>
      </div>
    </form>
  )
}
